import ContentHeader from 'components/UI/ContentHeader/ContentHeader';
import { MASTER_LINKS } from 'constants/master-list-tabs';
import AdvisoryService from 'services/AdvisoryService';
import { ReactQueryKeys } from 'constants/react-query-keys';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Outlet } from 'react-router-dom';
import { useState } from 'react';
import { ColumnFiltersState, PaginationState } from '@tanstack/react-table';
import { SweetAlertResult } from 'sweetalert2';
import SwalAlert, {
  firePreConfirmAlert,
} from 'components/UI/SwalAlert/SwalAlert';
import { toast } from 'react-hot-toast';
import { ADMINISTRATION, AUTHOR } from 'constants/roles';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AdvisoryList from './AdvisoryList/AdvisoryList';
import { Advisory } from 'interfaces/advisory';
import Spinner from 'components/UI/Spinner/Spinner';
import HospitalTotalLabels from 'components/UI/HospitalTotalLabels/HospitalTotalLabels';
import { selectUser } from 'store/slices/auth';

/**
 * @description Main component for displaying and managing advisory records.
 * This component provides a master-detail view where the left side shows a list of advisories
 * and the right side displays the details of the selected advisory via React Router's Outlet.
 * Features include pagination, filtering, search functionality, and deletion for authorized users.
 *
 * @SADtrace SAD-003-3.3
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @returns {JSX.Element} The rendered Advisories component
 */
const Advisories = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const user = useSelector(selectUser);
  // v8 ignore start -- WAIVER(MICRO): user.role is always present on an authenticated session; the '' fallback exists only to satisfy the optional user type
  const canManage = [ADMINISTRATION, AUTHOR].includes(user?.role || '');
  // v8 ignore stop -- WAIVER(MICRO)

  const {
    data: advisories,
    isLoading: isLoadingAdvisories,
    isFetching,
  } = useQuery({
    queryKey: [
      ReactQueryKeys.ADVISORIES,
      {
        columnFilters,
        pagination,
      },
    ],

    queryFn: () =>
      AdvisoryService.get({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sorting: [
          {
            id: 'name',
            desc: false,
          },
        ],
        columnFilters: columnFilters,
      }),

    placeholderData: keepPreviousData,
  });

  /**
   * @description Update search filters for the API call to filter advisories by name
   *
   * @SADtrace SAD-003-3.3
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string} value - The search value to filter advisories by name
   *
   * @returns {void}
   */
  const handleUpdateFilter = (value: string) => {
    setColumnFilters([
      {
        id: 'name',
        value,
      },
    ]);
  };

  /**
   * @description Shows a confirmation dialog for deleting an advisory.
   * If confirmed, deletes the advisory and updates the query cache
   *
   * @SADtrace SAD-003-3.3, SAD-003-3.5
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {Advisory} advisory - The advisory object to be deleted
   *
   * @returns {void}
   */
  const handleDeleteShow = (advisory: Advisory) => {
    firePreConfirmAlert({
      title: t('advisories.messages.modal__delete__title'),
      html: t('advisories.messages.modal__delete__content', {
        name: advisory.name,
      }),
      preConfirm: () => {
        return AdvisoryService.delete(String(advisory.id))
          .then(({ data }) => {
            return data;
          })
          .catch((error) => {
            SwalAlert.showValidationMessage(error?.response?.data?.message);
            //return false to prevent pop up from closing when running tests, check preconfirm fn
            return false;
          });
      },
    }).then((data: SweetAlertResult) => {
      // v8 ignore start -- WAIVER(THIRDPARTY): SweetAlert2 continuation — the dismissed arm resolves inside swal's promise contract with no application behavior to assert; the confirmed path is asserted
      if (data.isConfirmed) {
        // v8 ignore stop -- WAIVER(THIRDPARTY)
        queryClient.invalidateQueries({
          queryKey: [ReactQueryKeys.ADVISORIES],
        });
        toast.success(t('advisories.messages.delete__success'));
      }
    });
  };

  return (
    <>
      <ContentHeader links={MASTER_LINKS} />
      <div className="main-container with-tabs" data-testid="main-container">
        <div className="row">
          <div className="col-3">
            {isLoadingAdvisories ? (
              <Spinner isAbsolute />
            ) : (
              <>
                <HospitalTotalLabels
                  translationKey="advisories"
                  total={
                    // v8 ignore start -- WAIVER(MICRO): the catalog always contains the built-in basic advisory, so a zero/undefined total exists only as a type-level fallback
                    advisories?.data.total ? advisories?.data.total - 1 : 0
                    // v8 ignore stop -- WAIVER(MICRO)
                  }
                />
                <AdvisoryList
                  refetch={isLoadingAdvisories || isFetching}
                  canManage={canManage}
                  onDelete={handleDeleteShow}
                  updateFilter={handleUpdateFilter}
                  advisoriesResponse={advisories?.data}
                  pagination={pagination}
                  setPagination={setPagination}
                />
              </>
            )}
          </div>
          <div className="col-9 ps-4">
            <Outlet />
          </div>
        </div>
      </div>
    </>
  );
};

export default Advisories;
